'use client'

import type { SiteCopy } from '@/lib/site-copy'
import { pricingSectionBlockVariants } from '@tailark/core/marketing'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@tailark/core/ui/accordion'
import { AnimatedGroup } from '@tailark/core/ui/animated-group'
import Link from 'next/link'

type PricingFaqCopy = SiteCopy['pricingFaq']

export function PricingFaqSection({ copy }: { copy: PricingFaqCopy }) {
    return (
        <section className="relative pb-24 pt-8 md:pb-32 md:pt-12">
            <div className="relative z-10 mx-auto max-w-2xl px-4 md:px-6">
                <AnimatedGroup
                    variants={{
                        container: {
                            visible: { transition: { delayChildren: 0.35, staggerChildren: 0.1 } },
                        },
                        item: pricingSectionBlockVariants.item,
                    }}
                    className="flex flex-col">
                    <div className="mb-10 space-y-3 text-center md:mb-14">
                        <h2 className="text-foreground text-3xl font-semibold tracking-tight md:text-4xl">{copy.title}</h2>
                        <p className="text-muted-foreground mx-auto max-w-xl text-pretty text-sm leading-relaxed md:text-base">
                            {copy.subtitle}
                        </p>
                    </div>

                    <div className="ring-foreground/8 bg-card/80 rounded-2xl p-1 shadow-lg shadow-black/[0.04] ring-1 backdrop-blur-sm dark:shadow-black/20">
                        <Accordion
                            type="single"
                            collapsible
                            className="w-full">
                            {copy.items.map((item, index) => {
                                const id = `pricing-faq-${index}`
                                return (
                                    <div
                                        className="group"
                                        key={id}>
                                        <AccordionItem
                                            value={id}
                                            className="data-[state=open]:bg-muted/60 border-none px-4 py-0.5 first:rounded-t-xl last:rounded-b-xl data-[state=open]:shadow-none md:px-5">
                                            <AccordionTrigger className="cursor-pointer py-4 text-start text-base font-medium hover:no-underline md:text-[0.95rem]">
                                                {item.question}
                                            </AccordionTrigger>
                                            <AccordionContent className="text-muted-foreground pb-4 pt-0 text-sm leading-relaxed md:text-[0.9375rem]">
                                                {item.answer}
                                            </AccordionContent>
                                        </AccordionItem>
                                        <hr className="border-border/70 mx-4 group-last:hidden opacity-80 md:mx-5" />
                                    </div>
                                )
                            })}
                        </Accordion>
                    </div>

                    <p className="text-muted-foreground mt-8 text-center text-sm leading-relaxed">
                        {copy.footerBeforeLink}{' '}
                        <Link
                            href="/docs"
                            className="text-primary font-medium underline-offset-4 hover:underline">
                            {copy.docsCta}
                        </Link>{' '}
                        {copy.footerAfterLink}
                    </p>
                </AnimatedGroup>
            </div>
        </section>
    )
}
